import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getErrorMessage,
    useMutationWithHandler,
} from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { Paperclip } from 'lucide-react'
import { ReactNode, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog } from 'vnetwork-ui'
import { useSignedDocumentErrorText } from '../../hooks/use-signed-document-error-text'
import { SIGNED_DOCUMENTS } from '../../constants/signed-documents'
import {
    getSignedDocumentMailPrefill,
    sendSignedDocumentMail,
} from '../../services'
import { SignedDocument, SignedDocumentType } from '../../types'
import EmailTagInput, { isValidEmail } from './EmailTagInput'

interface SendMailDialogProps {
    open: boolean
    onClose: () => void
    documentType: SignedDocumentType
    invoiceID: number
    /** Response gửi mail (cùng cấu trúc GET trạng thái, đã có mails + last_sent_at) */
    onSent: (result: SignedDocument) => void
    /** Gửi lỗi: lần gửi lỗi vẫn được lưu, parent đọc lại để hiện trong lịch sử */
    onFailed: () => void
}

const Field = ({
    label,
    required,
    children,
}: {
    label: string
    required?: boolean
    children: ReactNode
}) => (
    <div className="grid grid-cols-[80px_1fr] items-start gap-3">
        <span className="pt-2 font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500"> *</span>}
        </span>
        {children}
    </div>
)

/**
 * Gửi chứng từ đã ký cho khách. Người nhận + tiêu đề điền sẵn từ
 * GET .../<path>/mail-prefill; backend tự đính kèm file PDF đã ký.
 */
const SendMailDialog = ({
    open,
    onClose,
    documentType,
    invoiceID,
    onSent,
    onFailed,
}: SendMailDialogProps) => {
    const { t } = useTranslation('invoice')
    const config = SIGNED_DOCUMENTS[documentType]
    const documentNames = {
        document: t(`${config.i18nKey}.name`),
        Document: t(`${config.i18nKey}.name-capitalized`),
    }
    const errorText = useSignedDocumentErrorText(documentType)

    const [to, setTo] = useState<string[]>([])
    const [cc, setCc] = useState<string[]>([])
    const [bcc, setBcc] = useState<string[]>([])
    const [subject, setSubject] = useState('')

    const prefill = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.INVOICE_SIGNED_DOCUMENT_MAIL_PREFILL,
                documentType,
                invoiceID,
            ],
            queryFn: () =>
                getSignedDocumentMailPrefill(documentType, invoiceID),
            // Luôn lấy mới: người nhận có thể vừa được sửa bên khách hàng
            gcTime: 0,
        },
        {
            renderError: (error) => (
                <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {errorText(getErrorMessage(error))}
                </div>
            ),
        }
    )
    const suggestion = prefill.data?.result

    useEffect(() => {
        if (!suggestion) return
        setTo(suggestion.to ?? [])
        setCc(suggestion.cc ?? [])
        setBcc(suggestion.bcc ?? [])
        setSubject(suggestion.subject ?? '')
    }, [suggestion])

    const { mutateAsync: send, isPending } = useMutationWithHandler(
        { mutationFn: sendSignedDocumentMail },
        {
            reactQueryKeys: QUERY_KEYS.INVOICE_SIGNED_DOCUMENT,
            onSuccess: (response) => {
                toast.success(t('signed-document-mail-sent'))
                if (response?.result) onSent(response.result)
                onClose()
            },
            // useMutationWithHandler không tra namespace invoice: tự toast bằng errorText
            showErrorToast: false,
            onError: (error) => {
                const code = getErrorMessage(error)
                // Cùng mã với Ký lại nhưng câu khác: "... không thể gửi mail"
                toast.error(
                    code === 'esign-request-not-signed'
                        ? t(
                              'signed_document_errors.esign-mail-not-signed',
                              documentNames
                          )
                        : errorText(code)
                )
                onFailed()
            },
        }
    )

    const allRecipients = [...to, ...cc, ...bcc]
    const hasInvalidEmail = allRecipients.some((email) => !isValidEmail(email))
    const canSend =
        !!suggestion &&
        to.length > 0 &&
        !!subject.trim() &&
        !hasInvalidEmail &&
        !isPending

    const renderForm = () =>
        prefill.renderState() ??
        (suggestion && (
            <div className="space-y-3 text-sm">
                <Field label={t('signed-document-mail-from')}>
                    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700">
                        {suggestion.from_name
                            ? `${suggestion.from_name} <${suggestion.from}>`
                            : suggestion.from}
                    </div>
                </Field>
                <Field label={t('signed-document-mail-to')} required>
                    <EmailTagInput
                        value={to}
                        onChange={setTo}
                        disabled={isPending}
                        placeholder={t(
                            'signed-document-mail-recipient-placeholder'
                        )}
                    />
                </Field>
                <Field label="CC">
                    <EmailTagInput
                        value={cc}
                        onChange={setCc}
                        disabled={isPending}
                    />
                </Field>
                <Field label="BCC">
                    <EmailTagInput
                        value={bcc}
                        onChange={setBcc}
                        disabled={isPending}
                    />
                </Field>
                <Field label={t('signed-document-mail-subject')} required>
                    <input
                        value={subject}
                        onChange={(event) => setSubject(event.target.value)}
                        disabled={isPending}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none"
                    />
                </Field>
                <div className="flex items-center gap-2 text-gray-600">
                    <Paperclip className="h-4 w-4" />
                    {t('signed-document-mail-attachment', {
                        fileName: config.fileName(invoiceID),
                    })}
                </div>
                {hasInvalidEmail && (
                    <div className="text-red-600">
                        {t('signed_document_errors.esign-mail-invalid-address')}
                    </div>
                )}
            </div>
        ))

    return (
        <Dialog
            open={open}
            onClose={() => !isPending && onClose()}
            maxWidth="md"
            title={t('signed-document-mail-title', documentNames)}
            actions={
                <div className="flex w-full items-center justify-end gap-2">
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('close')}
                    </Button>
                    <Button
                        onClick={() =>
                            send({
                                type: documentType,
                                invoiceID,
                                to,
                                cc,
                                bcc,
                                subject: subject.trim(),
                            })
                        }
                        loading={isPending}
                        disabled={!canSend}
                    >
                        {t('signed-document-mail-send')}
                    </Button>
                </div>
            }
        >
            {renderForm()}
        </Dialog>
    )
}

export default SendMailDialog
